/**
 * Figures for the methodology and analysis pages.
 *
 * Every function here is pure and takes the parsed data.json (or
 * advertisers.json) as an argument, so the prerenderer can run them at build
 * time and the browser can run the identical code after a client-side
 * navigation. The prose on those pages is written by hand; the numbers in it
 * are computed from the live dataset so they cannot go stale.
 */

function median(values) {
  const nums = values.filter(v => typeof v === 'number' && !Number.isNaN(v)).sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function round(value, places = 1) {
  if (value == null) return null;
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

/** Flatten data.episodes (keyed by podcast) into one array. */
export function allEpisodes(data) {
  return Object.values(data?.episodes ?? {}).flat();
}

/**
 * Episodes where "host share versus guest share" is a meaningful question.
 *
 * Excludes solo episodes, which have no guest, and panel shows, where every
 * regular is counted as a host and the combined figure approaches 100% whether
 * or not a guest was present. Comparing a four-host panel against a one-to-one
 * interview would put the panel at the top of the table for a structural
 * reason, not a behavioural one.
 */
function conversational(episodes) {
  return episodes.filter(e =>
    !e.solo_episode && !e.multi_host && e.host_pct != null && e.guest_pct != null);
}

/** Below this an average is noise rather than a pattern. */
const MIN_EPISODES = 5;

/* ───────────────────────── methodology ───────────────────────── */

// How each method reads in prose. Anything not listed is a human correction,
// which we group rather than list individually.
const METHOD_LABELS = {
  ad_reader: 'Who reads the ads',
  self_intro: 'Host introduces themselves',
  first_utterance: 'Guest opener ("thanks for having me")',
  name_match: 'Who never says the host name',
  co_host_name_match: 'Who never says the co-host name',
  co_host_self_intro: 'Co-host introduces themselves',
  question_count: 'Who asks the most questions (fallback)',
  single_speaker: 'Only one speaker present',
};

const MANUAL_METHODS = new Set([
  'manual_fix', 'verified_correct', 'transcript_review', 'speaker_id_named',
]);

/**
 * How the host was identified across the published catalogue, most used first.
 * Human corrections are collapsed into a single row, because the interesting
 * comparison is automatic method versus automatic method.
 */
export function resolutionBreakdown(data) {
  const episodes = allEpisodes(data);
  const total = episodes.length;
  const counts = new Map();
  let manual = 0;

  for (const ep of episodes) {
    const method = ep.resolution_method;
    if (!method) continue;
    if (MANUAL_METHODS.has(method)) { manual += 1; continue; }
    counts.set(method, (counts.get(method) ?? 0) + 1);
  }

  const rows = [...counts.entries()]
    .map(([method, count]) => ({
      method,
      label: METHOD_LABELS[method] ?? method,
      count,
      pct: round((count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  if (manual) {
    rows.push({
      method: 'manual',
      label: 'Corrected by hand after review',
      count: manual,
      pct: round((manual / total) * 100),
    });
  }

  return { total, rows };
}

/** Headline confidence numbers quoted in the methodology prose. */
export function confidenceSummary(data) {
  const episodes = allEpisodes(data);
  const conf = episodes.map(e => e.host_confidence).filter(c => typeof c === 'number');
  const high = conf.filter(c => c >= 0.9).length;
  const low = conf.filter(c => c < 0.75).length;
  return {
    episodes: episodes.length,
    podcasts: (data?.podcasts ?? []).length,
    medianConfidence: median(conf),
    highConfidencePct: round((high / conf.length) * 100),
    lowConfidenceCount: low,
  };
}

/* ───────────────────────── talk-time league ───────────────────────── */

/**
 * Per-podcast host talk share, ranked. Solo episodes are excluded: a host
 * talking 99% of a monologue episode says nothing about how they treat a guest.
 */
export function talkTimeLeague(data) {
  const byPodcast = new Map((data?.podcasts ?? []).map(p => [p.podcast_uuid, p]));
  const rows = [];

  for (const [uuid, episodes] of Object.entries(data?.episodes ?? {})) {
    const podcast = byPodcast.get(uuid);
    if (!podcast) continue;
    const eligible = conversational(episodes);
    if (eligible.length < MIN_EPISODES) continue;

    const shares = eligible.map(e => e.host_pct);
    rows.push({
      uuid,
      name: podcast.name,
      hostName: podcast.host_name,
      episodes: eligible.length,
      medianHostPct: round(median(shares)),
      minHostPct: round(Math.min(...shares)),
      maxHostPct: round(Math.max(...shares)),
    });
  }

  return rows.sort((a, b) => b.medianHostPct - a.medianHostPct);
}

/* ───────────────────────── interview dynamics ───────────────────────── */

/**
 * Question ratio and turn-taking per podcast, plus the record monologue.
 * Question ratio is host questions as a share of all questions asked, so 1.0
 * means only the host ever asked anything.
 */
export function interviewDynamics(data) {
  const byPodcast = new Map((data?.podcasts ?? []).map(p => [p.podcast_uuid, p]));
  const rows = [];

  for (const [uuid, episodes] of Object.entries(data?.episodes ?? {})) {
    const podcast = byPodcast.get(uuid);
    if (!podcast) continue;
    const eligible = conversational(episodes);
    if (eligible.length < MIN_EPISODES) continue;

    const ratios = eligible.map(e => e.question_ratio).filter(r => typeof r === 'number');
    const turns = eligible.map(e => e.turn_switches).filter(t => typeof t === 'number');
    const monologues = eligible
      .filter(e => typeof e.longest_monologue_ms === 'number')
      .sort((a, b) => b.longest_monologue_ms - a.longest_monologue_ms);

    rows.push({
      uuid,
      name: podcast.name,
      hostName: podcast.host_name,
      episodes: eligible.length,
      medianQuestionRatio: ratios.length ? round(median(ratios), 2) : null,
      medianTurnSwitches: turns.length ? Math.round(median(turns)) : null,
      longestMonologueMs: monologues[0]?.longest_monologue_ms ?? null,
      longestMonologueTitle: monologues[0]?.title ?? null,
    });
  }

  return rows
    .filter(r => r.medianQuestionRatio != null)
    .sort((a, b) => b.medianQuestionRatio - a.medianQuestionRatio);
}

/* ───────────────────────── advertisers ───────────────────────── */

/**
 * Brands ranked by how many ad reads we counted, with the number of distinct
 * shows they ran on. `reach` is the more interesting figure of the two: a brand
 * can rack up mentions on one heavy-sponsorship show without buying broadly.
 */
export function advertiserLeague(advertisers, limit = 25) {
  const entries = Object.entries(advertisers?.advertisers ?? {});
  const rows = entries.map(([key, value]) => ({
    key,
    name: value.name ?? key,
    total: value.total ?? 0,
    reach: Object.keys(value.podcasts ?? {}).length,
    topShow: Object.entries(value.podcasts ?? {}).sort((a, b) => b[1] - a[1])[0] ?? null,
  }));

  return {
    scanned: advertisers?.episodes_scanned ?? null,
    generatedAt: advertisers?.generated_at ?? null,
    brands: entries.length,
    rows: rows.sort((a, b) => b.total - a.total).slice(0, limit),
  };
}

export function formatMonologue(ms) {
  if (ms == null) return '';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
